import { z } from 'zod'
import {
  createListQuerySchema,
  ORDER_DIRECTIONS,
  paginationRequestSchema,
  paginationSchema,
  sharedFilterBySearchSchema,
  timestampsSchema,
} from './api'
import { metadataSchema } from './common/metadata'
import { remoteCommandSchema } from './sessions/macros'

export const automationIdSchema = z.string().uuid()
export type AutomationId = z.infer<typeof automationIdSchema>

const automationExecutionIdSchema = z.string().uuid()
export type AutomationExecutionId = z.infer<typeof automationExecutionIdSchema>

export const AUTOMATION_TRIGGER_TYPES = {
  event: 'event',
  schedule: 'schedule',
} as const

export const automationTriggerTypeSchema = z.nativeEnum(
  AUTOMATION_TRIGGER_TYPES
)
export type AutomationTriggerType = z.infer<typeof automationTriggerTypeSchema>

export const AUTOMATION_EXECUTION_STATUSES = {
  running: 'running',
  succeeded: 'succeeded',
  failed: 'failed',
} as const

export const automationExecutionStatusSchema = z.nativeEnum(
  AUTOMATION_EXECUTION_STATUSES
)
export type AutomationExecutionStatus = z.infer<
  typeof automationExecutionStatusSchema
>

export const AUTOMATION_HISTORY_ORDER_FIELDS = {
  startedAt: 'startedAt',
  completedAt: 'completedAt',
  status: 'status',
  triggerType: 'triggerType',
  automationName: 'automationName',
} as const

export const automationHistoryOrderFieldSchema = z.nativeEnum(
  AUTOMATION_HISTORY_ORDER_FIELDS
)
export type AutomationHistoryOrderField = z.infer<
  typeof automationHistoryOrderFieldSchema
>

export const repeatScheduleSchema = z.object({
  interval: z.number().min(60),
  endTime: z.number().min(1),
})
export type RepeatSchedule = z.infer<typeof repeatScheduleSchema>

export const LAUNCHABLE_RESOURCE_TARGET_TYPES = {
  id: 'id',
  metadata: 'metadata',
} as const

const launchableResourceIdTargetSchema = z.object({
  type: z.literal(LAUNCHABLE_RESOURCE_TARGET_TYPES.id),
  id: z.string().uuid(),
})

const launchableResourceMetadataTargetSchema = z.object({
  type: z.literal(LAUNCHABLE_RESOURCE_TARGET_TYPES.metadata),
  metadata: metadataSchema,
})

export const launchableResourceTargetSchema = z.discriminatedUnion('type', [
  launchableResourceIdTargetSchema,
  launchableResourceMetadataTargetSchema,
])
export type LaunchableResourceTarget = z.infer<
  typeof launchableResourceTargetSchema
>

export const AUTOMATION_ACTION_TYPES = {
  log: 'log',
  projectCommand: 'projectCommand',
  startProject: 'startProject',
  stopProject: 'stopProject',
} as const

export const logActionSchema = z.object({
  type: z.literal(AUTOMATION_ACTION_TYPES.log),
})

export const projectCommandActionSchema = z.object({
  type: z.literal(AUTOMATION_ACTION_TYPES.projectCommand),
  target: launchableResourceTargetSchema,
  command: remoteCommandSchema,
  componentId: z.string().nullish(),
})

export const startProjectActionSchema = z.object({
  type: z.literal(AUTOMATION_ACTION_TYPES.startProject),
  target: launchableResourceTargetSchema,
  enginePoolName: z.string().nullish(),
  regionName: z.string().nullish(),
  minSpecifications: z.unknown().nullish(),
  releaseChannel: z.string().nullish(),
})

export const stopProjectActionSchema = z.object({
  type: z.literal(AUTOMATION_ACTION_TYPES.stopProject),
  target: launchableResourceTargetSchema,
})

const fallbackActionSchema = z
  .object({
    type: z.string(),
  })
  .passthrough()

export const automationActionSchema = z.union([
  logActionSchema,
  projectCommandActionSchema,
  startProjectActionSchema,
  stopProjectActionSchema,
  fallbackActionSchema,
])
export type AutomationAction = z.infer<typeof automationActionSchema>

export const eventTriggerSchema = z.object({
  type: z.literal(AUTOMATION_TRIGGER_TYPES.event),
  filters: z.array(z.record(z.string(), z.unknown())),
})

export const scheduleTriggerSchema = z.object({
  type: z.literal(AUTOMATION_TRIGGER_TYPES.schedule),
  repeatSchedule: repeatScheduleSchema.nullable(),
  lastExecutedAt: z.number().nullable(),
  nextExecutionAt: z.number().nullable(),
})

export const automationTriggerSchema = z.discriminatedUnion('type', [
  eventTriggerSchema,
  scheduleTriggerSchema,
])
export type AutomationTrigger = z.infer<typeof automationTriggerSchema>

export const createEventTriggerSchema = z.object({
  type: z.literal(AUTOMATION_TRIGGER_TYPES.event),
  filters: z.array(z.record(z.string(), z.unknown())),
})

export const createScheduleTriggerSchema = z.object({
  type: z.literal(AUTOMATION_TRIGGER_TYPES.schedule),
  startTime: z.number(),
  repeatSchedule: repeatScheduleSchema.optional(),
})

export const createTriggerSchema = z.discriminatedUnion('type', [
  createEventTriggerSchema,
  createScheduleTriggerSchema,
])
export type CreateTrigger = z.infer<typeof createTriggerSchema>

export const AUTOMATION_ORDER_BY_FIELDS = [
  'name',
  'createdAt',
  'modifiedAt',
  'nextExecutionAt',
  'lastExecutedAt',
  'effectiveTime',
] as const

export const DEFAULT_AUTOMATION_ORDER = {
  field: 'effectiveTime',
  direction: ORDER_DIRECTIONS.ascending,
}

/** URL-persisted filter fields for the scheduled automations planner. */
export type ScheduledFilterBy = {
  search?: string
  isEnabled?: boolean
  effectiveTimeLower?: number
  effectiveTimeUpper?: number
  metadataKeySearch?: string
  metadataValueSearch?: string
}

export const DEFAULT_SCHEDULED_AUTOMATION_ORDER = DEFAULT_AUTOMATION_ORDER

const automationDetailsSchema = z
  .object({
    automationId: automationIdSchema,
    name: z.string(),
    description: z.string(),
    action: automationActionSchema,
    isEnabled: z.boolean(),
    trigger: automationTriggerSchema,
    lastExecutionStatus: automationExecutionStatusSchema.nullable(),
    metadata: metadataSchema.optional(),
  })
  .and(timestampsSchema)
export type AutomationDetails = z.infer<typeof automationDetailsSchema>

const listAutomationsResponseSchema = z
  .object({
    items: z.array(automationDetailsSchema),
  })
  .and(paginationSchema)
export type ListAutomationsResponse = z.infer<
  typeof listAutomationsResponseSchema
>

export const listAutomationsRequestSchema = createListQuerySchema(
  [
    'name',
    'createdAt',
    'modifiedAt',
    'nextExecutionAt',
    'lastExecutedAt',
    'effectiveTime',
  ],
  {
    orderBy: [DEFAULT_AUTOMATION_ORDER],
  }
).extend({
  filterBy: sharedFilterBySearchSchema
    .extend({
      triggerType: automationTriggerTypeSchema.optional(),
      isEnabled: z
        .preprocess(
          (v) => (v === 'true' ? true : v === 'false' ? false : v),
          z.boolean()
        )
        .optional(),
      effectiveTimeLower: z.coerce.number().int().optional(),
      effectiveTimeUpper: z.coerce.number().int().optional(),
    })
    .passthrough()
    .optional(),
})
export type ListAutomationsRequest = z.infer<
  typeof listAutomationsRequestSchema
>

/** Strict action union for create/update — no fallback passthrough. */
export const createAutomationActionSchema = z.discriminatedUnion('type', [
  logActionSchema,
  projectCommandActionSchema,
  startProjectActionSchema,
  stopProjectActionSchema,
])
export type CreateAutomationAction = z.infer<typeof createAutomationActionSchema>

export const createAutomationSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional().default(''),
  trigger: createTriggerSchema,
  action: createAutomationActionSchema,
  isEnabled: z.boolean().optional().default(true),
  metadata: metadataSchema.optional(),
})
export type CreateAutomation = z.infer<typeof createAutomationSchema>

const createAutomationResponseSchema = z.object({
  automationId: automationIdSchema,
})
export type CreateAutomationResponse = z.infer<
  typeof createAutomationResponseSchema
>

export const updateAutomationSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  action: createAutomationActionSchema.optional(),
  isEnabled: z.boolean().optional(),
  trigger: createTriggerSchema.optional(),
  metadata: metadataSchema.optional(),
})
export type UpdateAutomation = z.infer<typeof updateAutomationSchema>

export const executeAutomationSchema = z
  .record(z.string(), z.unknown())
  .nullable()
  .optional()
export type ExecuteAutomationRequest = z.infer<typeof executeAutomationSchema>

export const publishDataEventSchema = z.object({
  data: z.record(z.string(), z.unknown()),
})
export type PublishDataEvent = z.infer<typeof publishDataEventSchema>

const automationExecutionSchema = z.object({
  automationExecutionId: automationExecutionIdSchema,
  automationId: automationIdSchema.nullable(),
  automationName: z.string(),
  triggerType: automationTriggerTypeSchema,
  startedAt: z.number(),
  completedAt: z.number().nullable(),
  status: automationExecutionStatusSchema,
  errorMessage: z.string().nullable(),
})
export type AutomationExecution = z.infer<typeof automationExecutionSchema>

export const listExecutionHistoryRequestSchema = paginationRequestSchema.extend(
  {
    orderBy: z
      .array(
        z.object({
          field: automationHistoryOrderFieldSchema,
          direction: z.nativeEnum(ORDER_DIRECTIONS),
        })
      )
      .optional(),
    filterBy: sharedFilterBySearchSchema
      .extend({
        automationId: automationIdSchema.optional(),
        status: automationExecutionStatusSchema.optional(),
      })
      .passthrough()
      .optional(),
  }
)
export type ListExecutionHistoryRequest = z.infer<
  typeof listExecutionHistoryRequestSchema
>

const listExecutionHistoryResponseSchema = z
  .object({
    items: z.array(automationExecutionSchema),
  })
  .and(paginationSchema)
export type ListExecutionHistoryResponse = z.infer<
  typeof listExecutionHistoryResponseSchema
>
